import { isBrowser } from '@patrianna/shared-utils/helpers'

import type { ReduxState, TypedThunk } from 'src/store/types'
import { getUserIdSelector } from 'store/modules/user/selectors'

export const LOGGED_OUT_ID = 'logged-out'
export const IS_QUICK_START_OPENED = 'IS_QUICK_START_OPENED'
export const SAVED_UPCOMMING_GAMES = 'SAVED_UPCOMMING_GAMES'
export const LAST_DIALOG_OPEN = 'LAST_DIALOG_OPEN'
export const DELAYED_DIALOGS = 'DELAYED_DIALOGS'
export const LS_AUTH_TYPE = 'authType'
export const IS_USER_REGISTERED = 'IS_USER_REGISTERED'
export const IS_USER_OPENED_SC_RULES = 'IS_USER_OPENED_SC_RULES'
export const TABLE_GAMES_BANNERS_COUNTER = 'TABLE_GAMES_BANNERS_COUNTER'
export const IS_SIGN_UP_SESSION = 'IS_SIGN_UP_SESSION'

export const CLOSE_AUTO_PWA_DIALOG = 'CLOSE_AUTO_PWA_DIALOG'
export const HIDE_INSTALL_PWA_BUTTON = 'HIDE_INSTALL_PWA_BUTTON'
export const CAPTCHA_DISABLED = 'CAPTCHA_DISABLED'
export const IS_SUCCESS_PASSWORD_CHANGE = 'IS_SUCCESS_PASSWORD_CHANGE'
export const DYNAMO_UTM = 'DYNAMO_UTM'
export const DYNAMO_TOKEN = 'DYNAMO_TOKEN'
export const DYNAMO_UTM_CODE = 'DYNAMO_UTM_CODE'
export const PROCESSED_FREE_SPIN_IDS = 'processedFreeSpinIds'

const getSafeLocalStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') {
      return null
    }

    return window.localStorage
  } catch (err) {
    console.warn('[storage-safe] localStorage unavailable', err)

    return null
  }
}

export type LocalStorageKeys =
  | 'howToWinModal'
  | 'REFERRER'
  | 'QUERY'
  | 'AD_FIRST_TIME_OPENED'
  | 'ORDERED_OFFER'
  | 'REDEEM_PROVIDER'
  | 'NOT_ENOUGH_FUNDS'
  | 'LOW_COINS_DIALOG'
  | 'SESSION_ID'
  | 'USER_ID'
  | 'WELCOME_EMAIL_DIALOG_TIMER_ID'
  | 'FIRST_UTM'
  | 'LAST_UTM'
  | 'VISITED_BINGO_BANNER'
  | 'LS_FIRST_OPEN_SLOT'
  | 'TIME_OF_LINK_EXPIRATION'
  | typeof DELAYED_DIALOGS
  | typeof LAST_DIALOG_OPEN
  | typeof SAVED_UPCOMMING_GAMES
  | typeof IS_QUICK_START_OPENED
  | typeof LS_AUTH_TYPE
  | typeof IS_USER_REGISTERED
  | typeof CLOSE_AUTO_PWA_DIALOG
  | typeof HIDE_INSTALL_PWA_BUTTON
  | typeof IS_USER_OPENED_SC_RULES
  | typeof TABLE_GAMES_BANNERS_COUNTER
  | typeof CAPTCHA_DISABLED
  | typeof IS_SUCCESS_PASSWORD_CHANGE
  | typeof DYNAMO_UTM
  | typeof DYNAMO_TOKEN
  | typeof DYNAMO_UTM_CODE
  | typeof PROCESSED_FREE_SPIN_IDS
  | typeof IS_SIGN_UP_SESSION

const generateLsKey = (key: LocalStorageKeys, userId: string | null, personalized: boolean) => {
  const personalizedKey = userId ? `${key}-${userId}` : `${key}-${LOGGED_OUT_ID}`

  return personalized ? personalizedKey : key
}

// returns userID from localStorage or from redux store
function getUserId(state: ReduxState) {
  if (!isBrowser()) {
    return null
  }

  try {
    const userIdFromStore = getUserIdSelector(state)

    return userIdFromStore
  } catch (err) {
    console.log(err)

    return null
  }
}

export const setDataToLocalStorage =
  (key: LocalStorageKeys, value?: object | string | boolean, personalized = true): TypedThunk =>
  (_, getState: () => ReduxState) => {
    const userId = getUserId(getState())
    const lsKey = generateLsKey(key, userId, personalized)
    const storage = getSafeLocalStorage()

    try {
      storage?.setItem(lsKey, JSON.stringify(value))
    } catch (err) {
      console.log(err)
    }
  }

export const removeDataFromLocalStorage =
  (key: LocalStorageKeys, personalized = true): TypedThunk =>
  (_, getState: () => ReduxState) => {
    const userId = getUserId(getState())
    const lsKey = generateLsKey(key, userId, personalized)
    const storage = getSafeLocalStorage()

    try {
      storage?.removeItem(lsKey)
    } catch (err) {
      console.log(err)
    }
  }

export const getItemFormLocalStorageSelector = (state: ReduxState, key: LocalStorageKeys, personalized = true) => {
  try {
    const storage = getSafeLocalStorage()

    if (!storage) {
      return null
    }

    const userId = getUserId(state)
    const lsKey = generateLsKey(key, userId, personalized)

    const data = storage.getItem(lsKey)

    if (data && data !== 'undefined') {
      const result = JSON.parse(data)

      return result
    }

    return null
  } catch (err) {
    console.log(err)

    return null
  }
}

export const getSafeLocalStorageAccessor = getSafeLocalStorage
